import React from 'react';
import styled from 'styled-components';
import {EMPHASIS} from '@forecast-it/design-system';
import {LabelledButton} from './LabelledButton';
import {spin} from '../../../styles/animations';
import type {PendingAttachment} from '../hooks/useAttachmentUpload';

interface Props {
	attachments: PendingAttachment[];
	onRemove: (uploadId: string) => void;
}

function formatBytes(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const List = styled.div`
	margin-top: 8px;
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
`;
const Chip = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	border-radius: 9999px;
	border: 1px solid #e2e8f0;
	background: #f1f5f9;
	padding: 4px 10px;
	${p => p.theme.sys.font?.detail?.['1']}
	color: #334155;
`;
const Filename = styled.span`
	max-width: 160px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;
const Size = styled.span`
	flex-shrink: 0;
	color: #94a3b8;
`;
const Spinner = styled.span`
	display: inline-block;
	height: 12px;
	width: 12px;
	flex-shrink: 0;
	border-radius: 9999px;
	border: 2px solid #cbd5e1;
	border-top-color: #475569;
	animation: ${spin} 0.7s linear infinite;
`;

export function AttachmentList({attachments, onRemove}: Props) {
	if (attachments.length === 0) return null;

	return (
		<List>
			{attachments.map(attachment => (
				<Chip key={attachment.uploadId}>
					<Filename title={attachment.filename}>{attachment.filename}</Filename>
					<Size>{formatBytes(attachment.sizeBytes)}</Size>
					{attachment.progress < 100 && <Spinner aria-label="Uploading" />}
					<LabelledButton
						emphasis={EMPHASIS.LOW}
						label={`Remove ${attachment.filename}`}
						onPress={() => onRemove(attachment.uploadId)}
					>
						&times;
					</LabelledButton>
				</Chip>
			))}
		</List>
	);
}
